// src/pages/TestCategoriesPage.jsx
import { AddIcon, EditIcon, RepeatIcon } from '@chakra-ui/icons';
import {
    Badge, Box, Button, Card, CardBody, Checkbox, Divider, Flex, Heading, HStack, IconButton,
    Input, Menu, MenuButton, MenuItem, MenuList, Select, Spinner, Switch, Table,
    Tbody, Td, Text, Th, Thead, Tr, useDisclosure, useToast, VStack
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { FiMoreVertical, FiRotateCcw, FiTrash2 } from 'react-icons/fi';

import {
    createTestCategory,
    deleteTestCategory,
    getTestCategories,
    restoreTestCategory,
    updateTestCategory
} from '../../../api'; // adjust path if needed
import AccessDenied from '../../../components/AccessDenied';
import { hasPermission } from '../../../Utils';

const PageLimit = 20;

const TestCategoriesPage = () => {
    const toast = useToast();

    // permissions
    const canView = hasPermission('viewTestCategory');
    const canCreate = hasPermission('createTestCategory');
    const canUpdate = hasPermission('updateTestCategory');
    const canDelete = hasPermission('deleteTestCategory');
    const canRestore = hasPermission('restoreTestCategory');

    // list state
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [loading, setLoading] = useState(false);

    // filters
    const [q, setQ] = useState('');
    const [includeDeleted, setIncludeDeleted] = useState(false);
    const [parentFilter, setParentFilter] = useState(''); // '', 'null', or id
    const [isActiveFilter, setIsActiveFilter] = useState(''); // '', 'true', 'false'
    const [sort, setSort] = useState('displayOrder,name');

    // create/edit modal state
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editing, setEditing] = useState(null); // doc or null
    const [form, setForm] = useState({
        name: '',
        code: '',
        description: '',
        parentId: '',
        displayOrder: 0,
        isActive: true,
        labId: '', // optional
        baseSchema: null,
    });
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null); // id being deleted (soft)
    const [restoringId, setRestoringId] = useState(null); // id being restored
    const [error, setError] = useState(null);

    const queryString = useMemo(() => {
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(PageLimit));
        if (q) params.set('q', q);
        if (includeDeleted) params.set('includeDeleted', 'true');
        if (parentFilter) params.set('parentId', parentFilter);
        if (isActiveFilter) params.set('isActive', isActiveFilter);
        if (sort) params.set('sort', sort);
        return params.toString();
    }, [page, q, includeDeleted, parentFilter, isActiveFilter, sort]);

    const load = async () => {
        if (!canView) return;
        try {
            setLoading(true);
            const res = await getTestCategories(decodeURIComponent(queryString));
            if (res.success) {
                const payload = res?.data || res;
                setItems(payload.items || []);
                setTotal(payload.total || 0);
                setPage(payload.page || 1);
                setPages(payload.pages || 1);
            } else {
                setError(res.error);
            }
        } catch (err) {
            toast({ status: 'error', title: 'Load failed', description: err.message });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryString, canView]);

    const resetForm = () => {
        setForm({
            name: '',
            code: '',
            description: '',
            parentId: '',
            displayOrder: 0,
            isActive: true,
            labId: '',
            baseSchema: null,
        });
        setEditing(null);
    };

    const openCreate = () => {
        if (!canCreate) {
            toast({ status: 'warning', title: 'No permission to create' });
            return;
        }
        resetForm();
        onOpen();
    };

    const openEdit = (doc) => {
        if (!canUpdate) {
            toast({ status: 'warning', title: 'No permission to edit' });
            return;
        }
        setEditing(doc);
        setForm({
            name: doc.name || '',
            code: doc.code || '',
            description: doc.description || '',
            parentId: doc.parentId || '',
            displayOrder: typeof doc.displayOrder === 'number' ? doc.displayOrder : 0,
            isActive: typeof doc.isActive === 'boolean' ? doc.isActive : true,
            labId: doc.labId || '',
            baseSchema: doc.baseSchema ?? null,
        });
        onOpen();
    };

    const onSave = async () => {
        try {
            setSaving(true);
            const payload = {
                name: form.name?.trim(),
                code: form.code?.trim() || null,
                description: form.description?.trim() || '',
                parentId: form.parentId || null,
                displayOrder: Number(form.displayOrder) || 0,
                isActive: !!form.isActive,
                labId: form.labId || null,
                baseSchema: form.baseSchema ?? null,
            };

            if (editing && !canUpdate) {
                toast({ status: 'warning', title: 'No permission to edit' });
                return;
            }
            if (!editing && !canCreate) {
                toast({ status: 'warning', title: 'No permission to create' });
                return;
            }

            const res = editing
                ? await updateTestCategory(editing._id, payload)
                : await createTestCategory(payload);

            if (res?.success === false) throw new Error(res?.message || 'Save failed');

            toast({ status: 'success', title: editing ? 'Updated' : 'Created' });
            onClose();
            resetForm();
            load();
        } catch (err) {
            toast({ status: 'error', title: 'Save failed', description: err.message });
        } finally {
            setSaving(false);
        }
    };

    const onSoftDelete = async (id) => {
        if (!canDelete) {
            toast({ status: 'warning', title: 'No permission to delete' });
            return;
        }
        try {
            setDeletingId(id);
            const res = await deleteTestCategory(id, 'User requested removal');
            if (res?.success === false) throw new Error(res?.message || 'Delete failed');
            toast({ status: 'success', title: 'Soft deleted' });
            load();
        } catch (err) {
            toast({ status: 'error', title: 'Delete failed', description: err.message });
        } finally {
            setDeletingId(null);
        }
    };

    const onRestore = async (id) => {
        if (!canRestore) {
            toast({ status: 'warning', title: 'No permission to restore' });
            return;
        }
        try {
            setRestoringId(id);
            const res = await restoreTestCategory(id);
            if (res?.success === false) throw new Error(res?.message || 'Restore failed');
            toast({ status: 'success', title: 'Restored' });
            load();
        } catch (err) {
            toast({ status: 'error', title: 'Restore failed', description: err.message });
        } finally {
            setRestoringId(null);
        }
    };

    const parentOptions = useMemo(() => {
        return items.filter(i => !i.deletedAt);
    }, [items]);

    if (!canView) return <AccessDenied msg="You do not have permission to view test categories." />;
    if (error) return <AccessDenied msg={error} />;

    return (
        <Box p={5}>
            <Flex justify="space-between" align="center" mb={4}>
                <Heading size="lg">Test Categories</Heading>
                <HStack>
                    {canCreate && (
                        <Button leftIcon={<AddIcon />} colorScheme="blue" size={'xs'} onClick={openCreate}>
                            New Category
                        </Button>
                    )}
                    <IconButton size={'xs'} aria-label="refresh" icon={<RepeatIcon />} onClick={load} />
                </HStack>
            </Flex>

            <Card mb={4}>
                <CardBody>
                    <HStack spacing={3} flexWrap="wrap">
                        <Input
                            placeholder="Search..."
                            value={q}
                            onChange={(e) => { setPage(1); setQ(e.target.value); }}
                            maxW="280px"
                        />
                        <Select
                            value={parentFilter}
                            onChange={(e) => { setPage(1); setParentFilter(e.target.value); }}
                            maxW="260px"
                        >
                            <option value="">All parents</option>
                            <option value="null">Top-level only</option>
                            {parentOptions.map(p => (
                                <option key={p._id} value={p._id}>{p.name}</option>
                            ))}
                        </Select>
                        <Select
                            value={isActiveFilter}
                            onChange={(e) => { setPage(1); setIsActiveFilter(e.target.value); }}
                            maxW="200px"
                        >
                            <option value="">Any status</option>
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </Select>
                        <Select
                            value={sort}
                            onChange={(e) => { setPage(1); setSort(e.target.value); }}
                            maxW="260px"
                        >
                            <option value="displayOrder,name">Sort: Order, Name</option>
                            <option value="-createdAt">Newest</option>
                            <option value="name">Name A→Z</option>
                            <option value="-name">Name Z→A</option>
                        </Select>
                        <Checkbox
                            isChecked={includeDeleted}
                            onChange={(e) => { setPage(1); setIncludeDeleted(e.target.checked); }}
                        >
                            Include deleted
                        </Checkbox>
                    </HStack>
                </CardBody>
            </Card>

            <Card>
                <CardBody p={0}>
                    {loading ? (
                        <Flex align="center" justify="center" py={10}><Spinner /></Flex>
                    ) : (
                        <Table size="sm">
                            <Thead>
                                <Tr>
                                    <Th w="36px">#</Th>
                                    <Th>Name</Th>
                                    <Th>Code</Th>
                                    <Th>Parent</Th>
                                    <Th isNumeric>Order</Th>
                                    <Th>Active</Th>
                                    <Th>Scope</Th>
                                    <Th w="120px">Actions</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {items.map((it, idx) => {
                                    const row = (page - 1) * PageLimit + idx + 1;
                                    const parentName = parentOptions.find(p => p._id === it.parentId)?.name
                                        || (it.parentId ? '—' : 'Top');

                                    return (
                                        <Tr key={it._id} _hover={{ bg: 'gray.50' }}>
                                            <Td>{row}</Td>
                                            <Td>
                                                <HStack spacing={2}>
                                                    <Text fontWeight="semibold">{it.name || '-'}</Text>
                                                    {it.deletedAt ? <Badge colorScheme="red">Deleted</Badge> : null}
                                                </HStack>
                                                <Text fontSize="xs" color="gray.500">{it.description}</Text>
                                            </Td>
                                            <Td>{it.code || '-'}</Td>
                                            <Td>{parentName}</Td>
                                            <Td isNumeric>{typeof it.displayOrder === 'number' ? it.displayOrder : 0}</Td>
                                            <Td>{it.isActive ? 'Yes' : 'No'}</Td>
                                            <Td>{it.isGlobal ? 'Global' : (it.labId ? 'Lab' : '—')}</Td>
                                            <Td>
                                                <HStack spacing={1} justify="flex-end">
                                                    {!it.deletedAt ? (
                                                        <>
                                                            {canUpdate && (
                                                                <IconButton
                                                                    aria-label="edit"
                                                                    size="sm"
                                                                    icon={<EditIcon />}
                                                                    onClick={() => openEdit(it)}
                                                                />
                                                            )}
                                                            {canDelete && (
                                                                <Menu>
                                                                    <MenuButton as={IconButton} aria-label="more" size="sm" icon={<FiMoreVertical />} />
                                                                    <MenuList>
                                                                        <MenuItem
                                                                            icon={<FiTrash2 />}
                                                                            onClick={() => onSoftDelete(it._id)}
                                                                            isDisabled={deletingId === it._id}
                                                                        >
                                                                            {deletingId === it._id ? 'Deleting…' : 'Soft delete'}
                                                                        </MenuItem>
                                                                    </MenuList>
                                                                </Menu>
                                                            )}
                                                        </>
                                                    ) : (
                                                        canRestore && (
                                                            <Button
                                                                size="sm"
                                                                leftIcon={<FiRotateCcw />}
                                                                onClick={() => onRestore(it._id)}
                                                                isLoading={restoringId === it._id}
                                                            >
                                                                Restore
                                                            </Button>
                                                        )
                                                    )}
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                                {!items.length && !loading && (
                                    <Tr>
                                        <Td colSpan={8}>
                                            <Flex justify="center" py={10}>
                                                <Text color="gray.500">No categories</Text>
                                            </Flex>
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            {/* Pagination */}
            <Flex align="center" justify="space-between" mt={4}>
                <Text fontSize="sm" color="gray.600">
                    {total} total • Page {page} of {pages}
                </Text>
                <HStack>
                    <Button size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} isDisabled={page <= 1}>Prev</Button>
                    <Button size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} isDisabled={page >= pages}>Next</Button>
                </HStack>
            </Flex>

            {/* Create / Edit Modal */}
            {isOpen && (
                <Box
                    position="fixed"
                    inset="0"
                    bg="blackAlpha.500"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    zIndex={1000}
                >
                    <Card w="640px" maxW="96vw">
                        <CardBody>
                            <Heading size="md" mb={3}>{editing ? 'Edit Category' : 'New Category'}</Heading>
                            <VStack align="stretch" spacing={3}>
                                <Box>
                                    <Text fontSize="sm" mb={1}>Name</Text>
                                    <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb={1}>Code (optional)</Text>
                                    <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value }))} />
                                </Box>
                                <Box>
                                    <Text fontSize="sm" mb={1}>Description</Text>
                                    <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
                                </Box>
                                <HStack>
                                    <Box flex="1">
                                        <Text fontSize="sm" mb={1}>Parent</Text>
                                        <Select
                                            value={form.parentId || ''}
                                            onChange={(e) => setForm(f => ({ ...f, parentId: e.target.value || '' }))}
                                        >
                                            <option value="">Top level</option>
                                            {parentOptions.map(p => (
                                                <option key={p._id} value={p._id}>{p.name}</option>
                                            ))}
                                        </Select>
                                    </Box>
                                    <Box w="180px">
                                        <Text fontSize="sm" mb={1}>Display Order</Text>
                                        <Input
                                            type="number"
                                            value={form.displayOrder}
                                            onChange={(e) => setForm(f => ({ ...f, displayOrder: e.target.value }))}
                                        />
                                    </Box>
                                </HStack>
                                <HStack>
                                    <HStack flex="1">
                                        <Text>Active</Text>
                                        <Switch
                                            isChecked={form.isActive}
                                            onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                                        />
                                    </HStack>
                                    {/* <HStack flex="1">
                                        <Text>Global scope</Text>
                                        <Switch
                                            isChecked={!form.labId}
                                            onChange={(e) => setForm(f => ({ ...f, labId: e.target.checked ? '' : f.labId }))}
                                        />
                                    </HStack> */}
                                </HStack>
                            </VStack>

                            <Divider my={4} />
                            <HStack justify="flex-end">
                                <Button size={'sm'} variant="ghost" onClick={() => { onClose(); resetForm(); }} isDisabled={saving}>Cancel</Button>
                                <Button
                                    colorScheme="blue"
                                    size={"sm"}
                                    onClick={onSave}
                                    isLoading={saving}
                                    isDisabled={(editing && !canUpdate) || (!editing && !canCreate)}
                                >
                                    {editing ? 'Save Changes' : 'Create'}
                                </Button>
                            </HStack>
                        </CardBody>
                    </Card>
                </Box>
            )}
        </Box>
    );
};

export default TestCategoriesPage;
